/**
 * Centralized Error Handling Utility
 * 
 * Provides consistent error handling across the application without external logging services.
 * Handles errors gracefully and provides user-friendly feedback.
 */

import { toast } from '../common/Popover';

export interface ErrorContext {
    action?: string;
    userId?: string;
    adAccountId?: string;
    campaignId?: string;
    [key: string]: string | undefined;
}

/**
 * Error Handler class for centralized error management
 */
export class ErrorHandler {
    /**
     * Handle errors with optional context
     * Logs to console in development and shows user-friendly toast messages
     */
    static handleError(
        error: Error | unknown,
        userMessage: string,
        context?: ErrorContext
    ): void {
        // Log to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('Error occurred:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                error,
                context,
                timestamp: new Date().toISOString(),
            });
        }

        // Show user-friendly message
        toast('error', userMessage);
    }

    /**
     * Handle API errors specifically
     */
    static handleApiError(
        error: Error | unknown,
        endpoint: string,
        userMessage?: string
    ): void {
        const defaultMessage = 'An error occurred while fetching data';

        if (process.env.NODE_ENV === 'development') {
            console.error('API Error:', {
                endpoint,
                message: error instanceof Error ? error.message : 'Unknown error',
                error,
                timestamp: new Date().toISOString(),
            });
        }

        toast('error', userMessage || defaultMessage);
    }

    /**
     * Handle warnings (non-critical issues)
     */
    static handleWarning(message: string, context?: ErrorContext): void {
        if (process.env.NODE_ENV === 'development') {
            console.warn('Warning:', {
                message,
                context,
                timestamp: new Date().toISOString(),
            });
        }

        toast('warning', message);
    }

    /**
     * Log info messages (for debugging)
     */
    static logInfo(message: string, data?: Record<string, unknown>): void {
        if (process.env.NODE_ENV === 'development') {
            console.log('Info:', {
                message,
                data,
                timestamp: new Date().toISOString(),
            });
        }
    }

    /**
     * Extract error message from various error types
     */
    static getErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message;
        }
        if (typeof error === 'string') {
            return error;
        }
        return 'An unexpected error occurred';
    }
}

/**
 * Convenience function for handling errors
 */
export const handleError = ErrorHandler.handleError;

/**
 * Convenience function for handling API errors
 */
export const handleApiError = ErrorHandler.handleApiError;

/**
 * Convenience function for handling warnings
 */
export const handleWarning = ErrorHandler.handleWarning;
